import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isAtLeast } from '../utils/roles';
import { usePageAuthorization } from '../hooks/usePageAuthorization';
import { CollapsibleSection } from '../components/CollapsibleSection';
import { ExecutiveTitlesManager } from '../components/ExecutiveTitlesManager';
import { MembershipLevelsManager } from '../components/MembershipLevelsManager';
import { BudgetCategoriesManager } from '../components/BudgetCategoriesManager';
import { PageAccessControlSettings } from '../components/PageAccessControlSettings';
import { ManualRegistration } from '../components/ManualRegistration';
import { AdminKnowledgePanel } from '../components/AdminKnowledgePanel';
import { GenerateTestAccounts } from '../components/GenerateTestAccounts';
import { DataIntegrityModal } from '../components/DataIntegrityModal';
import { DashboardNavConfigPanel } from '../components/DashboardNavConfigPanel';
import { FocusedNavConfigPanel } from '../components/FocusedNavConfigPanel';
import { SmsConfigPanel } from '../components/SmsConfigPanel';
import { ThemeAdminPanel } from '../components/ThemeAdminPanel';
import { AdminImagePanel } from '../components/admin/AdminImagePanel';
import { getGxConfig, updateGxConfig } from '../api/guestExperience';
import { apiClient } from '../api/client';

function MergeDuplicateAccounts() {
  const [nameSearch, setNameSearch] = useState('');
  const [result, setResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleMerge = async () => {
    if (!nameSearch.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await apiClient('/users/merge-duplicate-accounts', {
        method: 'POST',
        body: { nameSearch: nameSearch.trim() },
      });
      setResult(data);
    } catch (e: any) {
      setError(e?.message || 'Failed to merge accounts');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <p style={{ fontSize: '0.9rem', color: '#555', marginBottom: '12px' }}>
        Search for a user by name to find and merge duplicate accounts. The account with registrations is kept; the duplicate is deactivated.
      </p>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
        <input
          type="text"
          value={nameSearch}
          onChange={(e) => setNameSearch(e.target.value)}
          placeholder="e.g. Elaine Turner"
          style={{ flex: 1, padding: '8px 12px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '13px' }}
        />
        <button
          onClick={handleMerge}
          disabled={loading || !nameSearch.trim()}
          style={{
            padding: '8px 16px',
            backgroundColor: '#1a6b3c',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: 600,
            cursor: loading ? 'wait' : 'pointer',
            opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? 'Merging...' : 'Merge'}
        </button>
      </div>
      {error && <p style={{ color: '#d32f2f', fontSize: '0.85rem' }}>❌ {error}</p>}
      {result && (
        <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #86efac', borderRadius: '6px', padding: '12px', fontSize: '0.85rem' }}>
          {result.success ? (
            <>
              <p style={{ margin: '0 0 6px', fontWeight: 600, color: '#166534' }}>✅ Merge complete</p>
              <p style={{ margin: '2px 0' }}><strong>Kept:</strong> {result.kept?.email} ({result.kept?.fullName})</p>
              <p style={{ margin: '2px 0' }}><strong>Deactivated:</strong> {result.deactivated?.email} ({result.deactivated?.fullName})</p>
              {result.log?.map((l: string, i: number) => <p key={i} style={{ margin: '2px 0', color: '#555' }}>• {l}</p>)}
            </>
          ) : (
            <p style={{ margin: 0 }}>{result.message || JSON.stringify(result)}</p>
          )}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '2rem', maxWidth: '900px', margin: '0 auto' },
  title: { color: '#1a6b3c', fontSize: '1.75rem', margin: '0 0 0.5rem 0' },
  subtitle: { color: '#666', fontSize: '0.95rem', marginBottom: '2rem' },
  accessDenied: {
    padding: '2rem',
    textAlign: 'center' as const,
    color: '#d32f2f',
  },
  navCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '1rem 1.25rem',
    marginBottom: '1rem',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    backgroundColor: '#fff',
    cursor: 'pointer',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },
  navCardIcon: {
    fontSize: '1.5rem',
  },
  navCardText: {
    flex: 1,
  },
  navCardTitle: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: '#1a6b3c',
    margin: 0,
  },
  navCardDesc: {
    fontSize: '0.8rem',
    color: '#666',
    margin: '0.15rem 0 0 0',
  },
  navCardArrow: {
    fontSize: '1rem',
    color: '#999',
  },
};

function SeedWisdomButton() {
  const [status, setStatus] = React.useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [result, setResult] = React.useState('');

  const handleSeed = async () => {
    setStatus('loading');
    try {
      const res = await apiClient<{ message: string; count: number }>('/krio-wisdom/seed', { method: 'POST' });
      setResult(`✅ ${res.message}`);
      setStatus('done');
    } catch (err: any) {
      setResult(`❌ Failed: ${err?.message || 'Unknown error'}`);
      setStatus('error');
    }
  };

  return (
    <div>
      <p style={{ fontSize: '0.9rem', color: '#555', marginBottom: '12px' }}>
        Load the 157 Krio Wisdom "Parable Parable" entries into the database. Safe to run multiple times (idempotent).
      </p>
      <button
        onClick={handleSeed}
        disabled={status === 'loading'}
        style={{
          padding: '10px 20px',
          backgroundColor: status === 'done' ? '#16a34a' : '#1a6b3c',
          color: '#fff',
          border: 'none',
          borderRadius: '6px',
          fontSize: '0.9rem',
          fontWeight: 600,
          cursor: status === 'loading' ? 'not-allowed' : 'pointer',
          opacity: status === 'loading' ? 0.6 : 1,
        }}
      >
        {status === 'loading' ? 'Seeding...' : status === 'done' ? 'Done ✓' : '🌱 Seed Wisdom Data'}
      </button>
      {result && <p style={{ marginTop: '8px', fontSize: '0.85rem', color: status === 'error' ? '#d32f2f' : '#16a34a' }}>{result}</p>}
    </div>
  );
}

function ProvisionGuestsButton() {
  const [status, setStatus] = React.useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [result, setResult] = React.useState('');

  const handleProvision = async () => {
    setStatus('loading');
    setResult('');
    try {
      // Get the Legacy Ball event (first event with "Legacy Ball" in name, or first event)
      const events = await apiClient<Array<{ id: string; name: string }>>('/events');
      const legacyBall = events.find(e => e.name.toLowerCase().includes('legacy ball')) || events[0];
      if (!legacyBall) {
        setStatus('error');
        setResult('No events found. Create an event first.');
        return;
      }
      const response = await apiClient<{ provisioning: { newlyProvisioned: number; totalProcessed: number; errors: string[] } }>(
        `/guest-accounts/provision/${legacyBall.id}`,
        { method: 'POST' },
      );
      const p = response.provisioning;
      setStatus('done');
      setResult(`✅ Provisioned ${p.newlyProvisioned} new accounts (${p.totalProcessed} processed)${p.errors.length > 0 ? ` — ${p.errors.length} errors` : ''}`);
    } catch (error: any) {
      setStatus('error');
      setResult(`❌ Failed: ${error.message || 'Unknown error'}`);
    }
  };

  return (
    <div>
      <p style={{ fontSize: '0.9rem', color: '#555', marginBottom: '12px' }}>
        Create login accounts for all imported guests who don't have one yet. Each guest gets a synthetic user account with their ticket number as the initial password.
      </p>
      <button
        onClick={handleProvision}
        disabled={status === 'loading'}
        style={{
          padding: '10px 20px',
          backgroundColor: status === 'done' ? '#16a34a' : status === 'loading' ? '#9ca3af' : '#1a6b3c',
          color: '#fff',
          border: 'none',
          borderRadius: '6px',
          fontSize: '0.9rem',
          fontWeight: 600,
          cursor: status === 'loading' ? 'not-allowed' : 'pointer',
        }}
      >
        {status === 'loading' ? 'Provisioning...' : status === 'done' ? 'Done ✓' : '👤 Provision Guest Accounts'}
      </button>
      {result && <p style={{ marginTop: '8px', fontSize: '0.85rem', color: status === 'error' ? '#d32f2f' : '#16a34a' }}>{result}</p>}
    </div>
  );
}

function WelcomePackageManager() {
  const [config, setConfig] = React.useState({ enabled: true, title: '', body: '', location: '', hours: '' });
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [msg, setMsg] = React.useState('');

  React.useEffect(() => {
    apiClient<any>('/my-weekend/welcome-package')
      .then((data) => setConfig({ enabled: data.enabled ?? true, title: data.title || '', body: data.body || '', location: data.location || '', hours: data.hours || '' }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setMsg('');
    try {
      await apiClient('/my-weekend/welcome-package', { method: 'PATCH', body: config });
      setMsg('Saved ✓');
    } catch (err: any) {
      setMsg(err?.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p style={{ fontSize: '0.85rem', color: '#666' }}>Loading...</p>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <p style={{ fontSize: '0.85rem', color: '#555', margin: 0 }}>
        This card appears on the Guest Toolkit page. Customize the message guests see about their welcome package pickup.
      </p>
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem' }}>
        <input type="checkbox" checked={config.enabled} onChange={(e) => setConfig({ ...config, enabled: e.target.checked })} />
        Enabled (visible to guests)
      </label>
      <input
        value={config.title}
        onChange={(e) => setConfig({ ...config, title: e.target.value })}
        placeholder="Card title"
        style={{ padding: '8px 10px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '13px' }}
      />
      <textarea
        value={config.body}
        onChange={(e) => setConfig({ ...config, body: e.target.value })}
        placeholder="Card body / instructions"
        rows={3}
        style={{ padding: '8px 10px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '13px', resize: 'vertical' }}
      />
      <input
        value={config.location}
        onChange={(e) => setConfig({ ...config, location: e.target.value })}
        placeholder="Location (e.g. Hotel lobby, Registration Desk)"
        style={{ padding: '8px 10px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '13px' }}
      />
      <input
        value={config.hours}
        onChange={(e) => setConfig({ ...config, hours: e.target.value })}
        placeholder="Hours (e.g. Friday 2 PM – 8 PM, Saturday 8 AM – 12 PM)"
        style={{ padding: '8px 10px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '13px' }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{ padding: '8px 16px', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer' }}
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
        {msg && <span style={{ fontSize: '0.82rem', color: msg.includes('✓') ? '#16a34a' : '#d32f2f' }}>{msg}</span>}
      </div>
    </div>
  );
}

function TableGuestsPrivacyToggle() {
  const [showTableGuests, setShowTableGuests] = React.useState(false);
  const [organizersBypass, setOrganizersBypass] = React.useState(true);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [msg, setMsg] = React.useState('');

  React.useEffect(() => {
    apiClient<{ showTableGuests: boolean; organizersBypassPrivacy: boolean }>('/my-weekend/privacy')
      .then((data) => {
        setShowTableGuests(data.showTableGuests);
        setOrganizersBypass(data.organizersBypassPrivacy);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (updates: { showTableGuests?: boolean; organizersBypassPrivacy?: boolean }) => {
    setSaving(true);
    setMsg('');
    try {
      const result = await apiClient<{ showTableGuests: boolean; organizersBypassPrivacy: boolean }>('/my-weekend/privacy', {
        method: 'PATCH',
        body: updates,
      });
      setShowTableGuests(result.showTableGuests);
      setOrganizersBypass(result.organizersBypassPrivacy);
      setMsg('Saved ✓');
    } catch (err: any) {
      setMsg(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p style={{ fontSize: '0.85rem', color: '#666' }}>Loading...</p>;

  return (
    <div>
      <p style={{ fontSize: '0.85rem', color: '#555', marginBottom: '12px' }}>
        Controls whether guests can see the names of other guests seated at their table on the My Weekend page.
        When disabled, guests not in the same order or table request group will appear as "Occupied".
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: saving ? 'not-allowed' : 'pointer' }}>
          <input
            type="checkbox"
            checked={showTableGuests}
            onChange={() => handleSave({ showTableGuests: !showTableGuests })}
            disabled={saving}
            style={{ width: '18px', height: '18px', accentColor: '#1a6b3c' }}
          />
          <span style={{ fontSize: '0.9rem', fontWeight: 500, color: '#333' }}>
            {showTableGuests ? 'All guest names visible to everyone' : 'Guest names restricted (order/group only)'}
          </span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: saving ? 'not-allowed' : 'pointer', marginLeft: '26px' }}>
          <input
            type="checkbox"
            checked={organizersBypass}
            onChange={() => handleSave({ organizersBypassPrivacy: !organizersBypass })}
            disabled={saving}
            style={{ width: '16px', height: '16px', accentColor: '#1a6b3c' }}
          />
          <span style={{ fontSize: '0.85rem', color: '#555' }}>
            Organizers & Admins always see all names (bypass privacy)
          </span>
        </label>
        {msg && <span style={{ fontSize: '0.82rem', color: msg.includes('✓') ? '#16a34a' : '#d32f2f' }}>{msg}</span>}
      </div>
    </div>
  );
}

export function AdminSettingsPage() {
  const { user } = useAuth();
  const { canEdit } = usePageAuthorization('admin-settings');
  const navigate = useNavigate();
  const [showIntegrityModal, setShowIntegrityModal] = useState(false);

  if (!user || !isAtLeast(user.role, 'Admin')) {
    return (
      <div style={styles.accessDenied}>
        <h2>Access Denied</h2>
        <p>You do not have permission to access admin settings.</p>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>Admin Settings</h1>
      <p style={styles.subtitle}>
        Manage reference data used across the application. Only administrators can modify these settings.
      </p>

      {!canEdit ? (
        <p style={{ color: '#666' }}>You do not have edit access to admin settings.</p>
      ) : (
        <>
          <div
            style={styles.navCard}
            onClick={() => navigate('/admin/document-library')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') navigate('/admin/document-library'); }}
          >
            <span style={styles.navCardIcon}>📚</span>
            <div style={styles.navCardText}>
              <p style={styles.navCardTitle}>Document Library</p>
              <p style={styles.navCardDesc}>Browse and manage system documentation</p>
            </div>
            <span style={styles.navCardArrow}>→</span>
          </div>

          <div
            style={styles.navCard}
            onClick={() => navigate('/admin/audit-history')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') navigate('/admin/audit-history'); }}
          >
            <span style={styles.navCardIcon}>📋</span>
            <div style={styles.navCardText}>
              <p style={styles.navCardTitle}>Audit History</p>
              <p style={styles.navCardDesc}>View and search the audit trail of data changes</p>
            </div>
            <span style={styles.navCardArrow}>→</span>
          </div>

          <CollapsibleSection title="Manual Registration">
            <ManualRegistration />
          </CollapsibleSection>

          <CollapsibleSection title="Page Access Control">
            <PageAccessControlSettings />
          </CollapsibleSection>

          <CollapsibleSection title="🎉 Public Dashboard Navigation">
            <DashboardNavConfigPanel onSaved={() => { /* Sidebar hook refetches on its own */ }} />
          </CollapsibleSection>

          <CollapsibleSection title="🎯 Focused Navigation">
            <FocusedNavConfigPanel />
          </CollapsibleSection>

          <CollapsibleSection title="📱 SMS Notifications">
            <SmsConfigPanel />
          </CollapsibleSection>

          <CollapsibleSection title="📱 Mobile Events View">
            <MobileEventsConfigPanel />
          </CollapsibleSection>

          <CollapsibleSection title="📱 Mobile Bottom Navigation">
            <MobileBottomNavConfigPanel />
          </CollapsibleSection>

          <CollapsibleSection title="🎨 Theme / Appearance">
            <ThemeAdminPanel />
          </CollapsibleSection>

          <CollapsibleSection title="📱 Attendee Panel Images">
            <AdminImagePanel />
          </CollapsibleSection>

          <CollapsibleSection title="🌱 Seed Krio Wisdom Data">
            <SeedWisdomButton />
          </CollapsibleSection>

          <CollapsibleSection title="👤 Provision Guest Accounts">
            <ProvisionGuestsButton />
          </CollapsibleSection>

          <CollapsibleSection title="🎁 Welcome Package Info">
            <WelcomePackageManager />
          </CollapsibleSection>

          <CollapsibleSection title="👥 Table Guests Visibility">
            <TableGuestsPrivacyToggle />
          </CollapsibleSection>

          <CollapsibleSection title="Data Integrity Audit">
            <p style={{ fontSize: '0.9rem', color: '#555', marginBottom: '12px' }}>
              Run an audit to detect orphaned records, duplicates, and data mismatches across guest, registration, ticket, and seat records.
            </p>
            <button
              onClick={() => setShowIntegrityModal(true)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#1a6b3c',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Run Data Integrity Audit
            </button>
          </CollapsibleSection>

          <CollapsibleSection title="Executive Titles">
            <ExecutiveTitlesManager />
          </CollapsibleSection>

          <CollapsibleSection title="Guest Experience Feed">
            <GxFeedConfigPanel />
          </CollapsibleSection>

          <CollapsibleSection title="Membership Levels">
            <MembershipLevelsManager />
          </CollapsibleSection>

          <CollapsibleSection title="Budget Categories">
            <BudgetCategoriesManager />
          </CollapsibleSection>

          <CollapsibleSection title="Knowledge Base">
            <AdminKnowledgePanel />
          </CollapsibleSection>

          <CollapsibleSection title="Generate Test Accounts">
            <GenerateTestAccounts />
          </CollapsibleSection>

          <CollapsibleSection title="🔀 Merge Duplicate Accounts">
            <MergeDuplicateAccounts />
          </CollapsibleSection>

          <CollapsibleSection title="🛡️ Disaster Recovery Plan">
            <p style={{ fontSize: '0.9rem', color: '#555', marginBottom: '12px' }}>
              Multi-machine warm standby plan for the reunion weekend. Contains database backup procedures, failover targets (Local Mac, Neon, Supabase), DNS switch instructions, and the decision tree for outage response.
            </p>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <a
                href="/docs/disaster-recovery-standby-plan.html"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  padding: '10px 20px',
                  backgroundColor: '#1a6b3c',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  textDecoration: 'none',
                }}
              >
                Open DR Plan
              </a>
              <button
                onClick={() => {
                  const w = window.open('/docs/disaster-recovery-standby-plan.html', '_blank');
                  if (w) { w.focus(); setTimeout(() => w.print(), 500); }
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#e0e0e0',
                  color: '#333',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                🖨️ Print DR Plan
              </button>
            </div>
          </CollapsibleSection>
        </>
      )}

      {showIntegrityModal && (
        <DataIntegrityModal onClose={() => setShowIntegrityModal(false)} />
      )}
    </div>
  );
}

// ── Mobile Events Config Panel ──────────────────────────────────────

function MobileEventsConfigPanel() {
  const [enabled, setEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [loadingConfig, setLoadingConfig] = useState(true);

  useEffect(() => {
    apiClient<{ enabled: boolean }>('/mobile-events-config')
      .then((config) => setEnabled(config.enabled))
      .catch(() => setEnabled(false))
      .finally(() => setLoadingConfig(false));
  }, []);

  const handleToggle = async () => {
    const newValue = !enabled;
    setSaving(true);
    setError('');
    try {
      const updated = await apiClient<{ enabled: boolean }>('/mobile-events-config', {
        method: 'PATCH',
        body: { enabled: newValue },
      });
      setEnabled(updated.enabled);
    } catch {
      setError('Failed to save. Try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loadingConfig) {
    return <p style={{ fontSize: '0.85rem', color: '#666' }}>Loading...</p>;
  }

  return (
    <div>
      <p style={{ fontSize: '0.9rem', color: '#555', marginBottom: '12px' }}>
        When enabled, users on mobile viewports (&lt;768px) will see a swipeable card-based events experience instead of the standard table view.
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#333' }}>
          Enable Mobile Events View
        </label>
        <button
          onClick={handleToggle}
          disabled={saving}
          style={{
            width: '48px',
            height: '26px',
            borderRadius: '13px',
            border: 'none',
            backgroundColor: enabled ? '#1a6b3c' : '#ccc',
            position: 'relative',
            cursor: saving ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.2s',
            opacity: saving ? 0.7 : 1,
          }}
          aria-pressed={enabled}
          aria-label="Toggle mobile events view"
        >
          <span
            style={{
              position: 'absolute',
              top: '3px',
              left: enabled ? '25px' : '3px',
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              backgroundColor: '#fff',
              transition: 'left 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }}
          />
        </button>
        <span style={{ fontSize: '0.8rem', color: '#666' }}>
          {enabled ? 'Enabled' : 'Disabled'}
        </span>
      </div>
      {error && <p style={{ fontSize: '0.8rem', color: '#d32f2f', marginTop: '8px' }}>{error}</p>}
    </div>
  );
}

// ── GX Feed Config Panel ──────────────────────────────────────

function MobileBottomNavConfigPanel() {
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [loadingConfig, setLoadingConfig] = useState(true);

  useEffect(() => {
    apiClient<{ enabled: boolean }>('/mobile-bottom-nav-config')
      .then((config) => setEnabled(config.enabled))
      .catch(() => setEnabled(true))
      .finally(() => setLoadingConfig(false));
  }, []);

  const handleToggle = async () => {
    const newValue = !enabled;
    setSaving(true);
    setError('');
    try {
      const updated = await apiClient<{ enabled: boolean }>('/mobile-bottom-nav-config', {
        method: 'PATCH',
        body: { enabled: newValue },
      });
      setEnabled(updated.enabled);
    } catch {
      setError('Failed to save. Try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loadingConfig) {
    return <p style={{ fontSize: '0.85rem', color: '#666' }}>Loading...</p>;
  }

  return (
    <div>
      <p style={{ fontSize: '0.9rem', color: '#555', marginBottom: '12px' }}>
        When enabled, mobile users (&lt;1024px) will see a persistent bottom navigation bar with quick links to Home, Events, Toolkit, Booklet, and Announcements.
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#333' }}>
          Enable Mobile Bottom Navigation
        </label>
        <button
          onClick={handleToggle}
          disabled={saving}
          style={{
            width: '48px',
            height: '26px',
            borderRadius: '13px',
            border: 'none',
            backgroundColor: enabled ? '#1a6b3c' : '#ccc',
            position: 'relative',
            cursor: saving ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.2s',
            opacity: saving ? 0.7 : 1,
          }}
          aria-pressed={enabled}
          aria-label="Toggle mobile bottom navigation"
        >
          <span
            style={{
              position: 'absolute',
              top: '3px',
              left: enabled ? '25px' : '3px',
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              backgroundColor: '#fff',
              transition: 'left 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }}
          />
        </button>
        <span style={{ fontSize: '0.8rem', color: '#666' }}>
          {enabled ? 'Enabled' : 'Disabled'}
        </span>
      </div>
      {error && <p style={{ fontSize: '0.8rem', color: '#d32f2f', marginTop: '8px' }}>{error}</p>}
    </div>
  );
}

// ── GX Feed Config Panel ──────────────────────────────────────

function GxFeedConfigPanel() {
  const [feedName, setFeedName] = useState('Kongosa Box');
  const [muted, setMuted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getGxConfig()
      .then((config) => {
        setFeedName(config.feedName);
        setMuted(config.muted);
      })
      .catch(() => {
        // Use defaults
      });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const updated = await updateGxConfig({ feedName, muted });
      setFeedName(updated.feedName);
      setMuted(updated.muted);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('Failed to save config');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div>
        <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#333', marginBottom: '4px' }}>
          Feed Page Name
        </label>
        <input
          type="text"
          value={feedName}
          onChange={(e) => setFeedName(e.target.value)}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #ccc',
            borderRadius: '6px',
            fontSize: '0.9rem',
            boxSizing: 'border-box',
          }}
          placeholder="Kongosa Box"
        />
        <p style={{ fontSize: '0.75rem', color: '#999', margin: '4px 0 0 0' }}>
          This name appears as the page title on the live feed page.
        </p>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#333' }}>
          Mute Feed
        </label>
        <button
          onClick={() => setMuted(!muted)}
          style={{
            width: '48px',
            height: '26px',
            borderRadius: '13px',
            border: 'none',
            backgroundColor: muted ? '#1a6b3c' : '#ccc',
            position: 'relative',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
          }}
          aria-pressed={muted}
          aria-label="Toggle mute feed"
        >
          <span
            style={{
              position: 'absolute',
              top: '3px',
              left: muted ? '25px' : '3px',
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              backgroundColor: '#fff',
              transition: 'left 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }}
          />
        </button>
        <span style={{ fontSize: '0.8rem', color: '#666' }}>
          {muted ? 'Feed is muted' : 'Feed is active'}
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '10px 20px',
            backgroundColor: '#1a6b3c',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: 600,
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
        {saved && <span style={{ fontSize: '0.8rem', color: '#2e7d32' }}>✓ Saved</span>}
        {error && <span style={{ fontSize: '0.8rem', color: '#d32f2f' }}>{error}</span>}
      </div>
    </div>
  );
}
